import { AbsenceBalance } from "@/api/AbsenceBalance";
import { DialogBody, DialogCloseTrigger, DialogContent, DialogFooter, DialogHeader, DialogRoot } from "@/components/ui/dialog";
import { Fieldset, Group, Heading, Textarea } from "@chakra-ui/react";
import { useEffect } from "react";
import { SubmitHandler, useForm } from "react-hook-form";
import { User } from "@/api/User";
import { format } from "date-fns";
import { AmountInputController } from "@/components/fields/AmountInput";
import { BiCalendarAlt } from "react-icons/bi";
import { Field } from "@/components/ui/field";
import CancelButton from "@/components/buttons/CancelButton";
import SubmitButton from "@/components/buttons/SubmitButton";
import { deLocaleOption } from "@/data/DateTimeUtil";

interface AbsenceBalanceDialogProps {
    isOpen: boolean;
    onClose: () => void;
    user: User;
    entry?: AbsenceBalance;
    onSave: (data: AbsenceBalance) => void;
}

const AbsenceBalanceDialog = ({ isOpen, onClose, user, entry, onSave }: AbsenceBalanceDialogProps) => {
    const emptyEntry: AbsenceBalance = {
        id: -1,
        days: 1,
        comments: null,
        userId: user?.id,
        organizationId: user?.organizationId,
        createdAt: new Date(),
    };
    const {
        register,
        control,
        handleSubmit,
        reset,
        formState: { errors, isSubmitting },
        watch,
    } = useForm<AbsenceBalance>({
        defaultValues: entry ?? emptyEntry
    });

    useEffect(() => {
        reset(entry ?? emptyEntry);
    }, [entry, reset, isOpen, user]);

    const onSubmit: SubmitHandler<AbsenceBalance> = (data) => {
        onSave(data);
        onClose();
    };

    return (
        <DialogRoot open={isOpen} onOpenChange={() => onClose()} size='xs' placement='center' motionPreset='scale'>
            <DialogContent>
                <form noValidate onSubmit={handleSubmit(onSubmit)}>
                    <DialogHeader>
                        <Heading textAlign="left" size="md">{format(entry?.createdAt ?? new Date(), 'EEEE d. MMMM', deLocaleOption)}</Heading>
                        <Heading textAlign='center' size='md'>
                            {entry?.id > -1 ? 'Anpassungen Bearbeiten' : 'Neue Anpassungen'}
                        </Heading>
                    </DialogHeader>
                    <DialogCloseTrigger />

                    <DialogBody>
                        <Fieldset.Root>
                            <AmountInputController w='6rem' control={control} name='days' decimalPlaces={1} readOnly={false} label='Ferien anpassen um' icon={<BiCalendarAlt color='blue' />} />
                            <Field label='Anmerkungen' invalid={!!errors.comments} errorText={errors.comments?.message}>
                                <Textarea
                                    placeholder='Kommentar hinzufügen...'
                                    {...register("comments")}
                                />
                            </Field>
                        </Fieldset.Root>
                    </DialogBody>
                    <DialogFooter>
                        <Group gap={2} flexWrap="wrap" justifyContent="flex-end" w="full">
                            <CancelButton onClick={onClose} />
                            <SubmitButton isSubmitting={isSubmitting} />
                        </Group>
                    </DialogFooter>
                </form>
            </DialogContent>
        </DialogRoot>
    );
}

export default AbsenceBalanceDialog;